
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, Calendar, User, Clock } from 'lucide-react';
import { useAthleteTaskNotifications } from '@/hooks/useAthleteTaskNotifications';

interface TaskReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TaskReviewModal: React.FC<TaskReviewModalProps> = ({
  isOpen,
  onClose
}) => {
  const { notifications, acceptTask, declineTask } = useAthleteTaskNotifications();

  const formatActivityType = (type: string) => {
    const types: { [key: string]: string } = {
      email: 'Send Email',
      call: 'Make Phone Call',
      meeting: 'Schedule Meeting',
      application: 'Submit Application',
      visit: 'Campus Visit',
      followup: 'Follow Up',
      research: 'Research',
      other: 'Other'
    };
    return types[type] || type;
  };

  const handleAccept = async (notificationId: string, taskId: string) => {
    const success = await acceptTask(notificationId, taskId);
    if (success && notifications.length === 1) {
      onClose();
    }
  };

  const handleDecline = async (notificationId: string, taskId: string) => {
    const success = await declineTask(notificationId, taskId);
    if (success && notifications.length === 1) {
      onClose();
    }
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Clock className="h-5 w-5" />
            <span>Task Review ({notifications.length} pending)</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            You have been assigned new recruiting tasks that require your approval.
          </p>

          {notifications.map((notification) => (
            <Card key={notification.id} className="border-orange-200">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">{notification.task.subject}</CardTitle>
                    <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                      <div className="flex items-center space-x-1">
                        <User className="h-3 w-3" />
                        <span>From {notification.scout.name}</span>
                      </div>
                      <Badge variant="outline">
                        {formatActivityType(notification.task.activity_type)}
                      </Badge>
                      {notification.task.activity_date && (
                        <div className="flex items-center space-x-1">
                          <Calendar className="h-3 w-3" />
                          <span>Due {new Date(notification.task.activity_date).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {notification.task.description && (
                  <div>
                    <h4 className="text-sm font-medium mb-1">Description</h4>
                    <p className="text-sm text-muted-foreground">
                      {notification.task.description}
                    </p>
                  </div>
                )}

                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDecline(notification.id, notification.task.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Decline
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleAccept(notification.id, notification.task.id)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Accept & Add to Tracker
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}

          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={onClose}>
              Review Later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TaskReviewModal;
